import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import ScopeIndicator from '../components/ScopeIndicator';
import ChildImagesModal from '../components/ChildImagesModal';
import BulkImportModal from '../components/BulkImportModal';
import { useAuth } from '../hooks/useAuth';
import { childAPI, classroomAPI, scheduleAPI } from '../utils/api';
import type { Child, CreateChildRequest, UpdateChildRequest } from '../types/child';
import type { Classroom } from '../types/classroom';
import type { Schedule } from '../types/schedule';

const emptyForm = (nurseryId: string): CreateChildRequest => ({
  name: '',
  email: '',
  phone: '',
  classroom_id: 0,
  schedule_id: undefined,
  nursery_id: nurseryId,
  date_of_birth: '',
  gender: '',
  enrollment_date: '',
  allergies: '',
  emergency_contact: '',
});

const getRelatedName = (value?: string | { name?: string } | null) => {
  if (!value) return '';
  return typeof value === 'string' ? value : value.name || '';
};

const formatDateForInput = (value?: string | null) => {
  if (!value) return '';
  const match = value.match(/^\d{4}-\d{2}-\d{2}/);
  return match ? match[0] : value;
};

const Children: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'children' });
  const { t: tc } = useTranslation('common');
  const { currentNursery } = useAuth();
  const nurseryId = currentNursery?.nursery_id || '';

  const [children, setChildren] = useState<Child[]>([]);
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState<Child | null>(null);
  const [formData, setFormData] = useState<CreateChildRequest>(emptyForm(''));
  const [imageChild, setImageChild] = useState<Child | null>(null);

  const loadData = useCallback(async () => {
    if (!nurseryId) return;
    setLoading(true);
    setError(null);
    try {
      const [childrenResponse, classroomsResponse, schedulesResponse] = await Promise.all([
        childAPI.getChildren(nurseryId, true),
        classroomAPI.getClassrooms(nurseryId),
        scheduleAPI.getSchedules(nurseryId),
      ]);
      setChildren(childrenResponse.children || []);
      setClassrooms(classroomsResponse.classrooms || []);
      setSchedules(schedulesResponse.schedules || []);
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('messages.error'));
    } finally {
      setLoading(false);
    }
  }, [nurseryId, tc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredChildren = useMemo(() => {
    const term = search.trim().toLowerCase();
    return children.filter((child) => (
      !term ||
      child.name?.toLowerCase().includes(term) ||
      getRelatedName(child.classroom).toLowerCase().includes(term) ||
      child.gender?.toLowerCase().includes(term)
    ));
  }, [children, search]);

  const openCreate = () => {
    setEditing(null);
    setFormData(emptyForm(nurseryId));
    setShowModal(true);
  };

  const openEdit = (child: Child) => {
    setEditing(child);
    setFormData({
      name: child.name,
      email: child.email || '',
      phone: child.phone || '',
      classroom_id: child.classroom_id || (typeof child.classroom === 'object' ? child.classroom.id : 0),
      schedule_id: child.schedule_id,
      nursery_id: nurseryId,
      date_of_birth: formatDateForInput(child.date_of_birth),
      gender: child.gender || '',
      enrollment_date: formatDateForInput(child.enrollment_date),
      allergies: child.allergies || '',
      emergency_contact: child.emergency_contact || '',
    });
    setShowModal(true);
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!nurseryId) return;

    setSaving(true);
    setError(null);
    try {
      if (editing) {
        const payload: UpdateChildRequest = {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          classroom_id: formData.classroom_id,
          schedule_id: formData.schedule_id,
          date_of_birth: formatDateForInput(formData.date_of_birth),
          gender: formData.gender,
          enrollment_date: formatDateForInput(formData.enrollment_date),
          allergies: formData.allergies,
          emergency_contact: formData.emergency_contact,
        };
        await childAPI.updateChild(editing.id, payload, nurseryId);
      } else {
        await childAPI.createChild({ ...formData, nursery_id: nurseryId });
      }
      setShowModal(false);
      await loadData();
    } catch (err: any) {
      setError(err?.response?.data?.message || err?.message || tc('messages.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout>
      <Container fluid className="py-4">
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h2 className="fw-bold text-primary mb-0">{t('title', { defaultValue: 'Children' })}</h2>
          </div>
          <div className="d-flex gap-2">
            <Button variant="outline-primary" onClick={() => setShowImport(true)} disabled={!nurseryId}>{t('bulkImport', { defaultValue: 'Import' })}</Button>
            <Button onClick={openCreate} disabled={!nurseryId}>{t('addChild', { defaultValue: 'Add Child' })}</Button>
          </div>
        </div>

        <ScopeIndicator resource="children" />
        {currentNursery ? (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title', { defaultValue: 'Children' }).toLowerCase() })}</strong> {currentNursery.description || currentNursery.name} ({tc('labels.id')}: {currentNursery.nursery_id})
          </Alert>
        ) : (
          <Alert variant="warning">{t('selectNursery', { defaultValue: 'Select a nursery to manage children.' })}</Alert>
        )}
        {error && <Alert variant="danger">{error}</Alert>}

        <Card className="shadow-sm border-0 mb-3">
          <Card.Body>
            <Form.Control value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('searchPlaceholder', { defaultValue: 'Search children or classrooms' })} />
          </Card.Body>
        </Card>

        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <div className="table-responsive">
              <Table hover className="mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th>{t('childName', { defaultValue: 'Child' })}</th>
                    <th>{t('classroom', { defaultValue: 'Classroom' })}</th>
                    <th>{t('schedule', { defaultValue: 'Schedule' })}</th>
                    <th>{t('dateOfBirth', { defaultValue: 'Date of birth' })}</th>
                    <th>{t('gender', { defaultValue: 'Gender' })}</th>
                    <th>{t('enrollmentDate', { defaultValue: 'Enrollment' })}</th>
                    <th>{t('images', { defaultValue: 'Images' })}</th>
                    <th className="text-end">{tc('labels.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={8} className="text-center py-5"><Spinner animation="border" /></td></tr>
                  ) : filteredChildren.length === 0 ? (
                    <tr><td colSpan={8} className="text-center text-muted py-5">{t('noneRegistered', { defaultValue: 'No children registered.' })}</td></tr>
                  ) : filteredChildren.map((child) => (
                    <tr key={child.id}>
                      <td>
                        <div className="fw-semibold">{child.name}</div>
                        {child.allergies && <Badge bg="warning" text="dark">{t('allergies', { defaultValue: 'Allergies' })}</Badge>}
                      </td>
                      <td>{getRelatedName(child.classroom) || '-'}</td>
                      <td>{getRelatedName(child.schedule) || '-'}</td>
                      <td>{formatDateForInput(child.date_of_birth) || '-'}</td>
                      <td>{child.gender || '-'}</td>
                      <td>{formatDateForInput(child.enrollment_date) || '-'}</td>
                      <td>{child.images_count ?? 0}</td>
                      <td className="text-end">
                        <div className="d-flex gap-2 justify-content-end">
                          <Button variant="outline-secondary" size="sm" onClick={() => setImageChild(child)}>{t('images', { defaultValue: 'Images' })}</Button>
                          <Button variant="outline-primary" size="sm" onClick={() => openEdit(child)}>{tc('buttons.edit')}</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>

        <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
          <Form onSubmit={handleSave}>
            <Modal.Header closeButton>
              <Modal.Title>{editing ? t('editChild', { defaultValue: 'Edit Child' }) : t('addChild', { defaultValue: 'Add Child' })}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <Row>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('nameLabel', { defaultValue: 'Name' })}</Form.Label><Form.Control required value={formData.name} onChange={(event) => setFormData({ ...formData, name: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('gender', { defaultValue: 'Gender' })}</Form.Label><Form.Control required value={formData.gender} onChange={(event) => setFormData({ ...formData, gender: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('dateOfBirth', { defaultValue: 'Date of birth' })}</Form.Label><Form.Control required type="date" value={formData.date_of_birth} onChange={(event) => setFormData({ ...formData, date_of_birth: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('enrollmentDate', { defaultValue: 'Enrollment date' })}</Form.Label><Form.Control required type="date" value={formData.enrollment_date} onChange={(event) => setFormData({ ...formData, enrollment_date: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('classroom', { defaultValue: 'Classroom' })}</Form.Label><Form.Select required value={formData.classroom_id || ''} onChange={(event) => setFormData({ ...formData, classroom_id: Number(event.target.value) })}><option value="">{tc('buttons.select')}</option>{classrooms.map((classroom) => <option key={classroom.id} value={classroom.id}>{classroom.name}</option>)}</Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('schedule', { defaultValue: 'Schedule' })}</Form.Label><Form.Select value={formData.schedule_id || ''} onChange={(event) => setFormData({ ...formData, schedule_id: event.target.value ? Number(event.target.value) : undefined })}><option value="">{tc('buttons.select')}</option>{schedules.map((schedule) => <option key={schedule.id} value={schedule.id}>{schedule.name}</option>)}</Form.Select></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('emailLabel', { defaultValue: 'Email' })}</Form.Label><Form.Control type="email" value={formData.email || ''} onChange={(event) => setFormData({ ...formData, email: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('phoneLabel', { defaultValue: 'Phone' })}</Form.Label><Form.Control value={formData.phone || ''} onChange={(event) => setFormData({ ...formData, phone: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('allergies', { defaultValue: 'Allergies' })}</Form.Label><Form.Control as="textarea" rows={2} value={formData.allergies || ''} onChange={(event) => setFormData({ ...formData, allergies: event.target.value })} /></Form.Group></Col>
                <Col md={6}><Form.Group className="mb-3"><Form.Label>{t('emergencyContact', { defaultValue: 'Emergency contact' })}</Form.Label><Form.Control as="textarea" rows={2} value={formData.emergency_contact || ''} onChange={(event) => setFormData({ ...formData, emergency_contact: event.target.value })} /></Form.Group></Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowModal(false)}>{tc('buttons.cancel')}</Button>
              <Button type="submit" disabled={saving}>{saving ? <Spinner animation="border" size="sm" /> : tc('buttons.save')}</Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <BulkImportModal show={showImport} onHide={() => setShowImport(false)} nurseryId={nurseryId} onSuccess={loadData} />
        {imageChild && (
          <ChildImagesModal
            show={!!imageChild}
            onHide={() => setImageChild(null)}
            nurseryId={nurseryId}
            childId={imageChild.id}
            childName={imageChild.name}
            onSuccess={loadData}
          />
        )}
      </Container>
    </Layout>
  );
};

export default Children;
